/**
 * Table of Contents component for documentation pages
 */

import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

interface Heading {
  id: string;
  text: string;
  level: number;
}

export function TableOfContents() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    // Reset when route changes
    setHeadings([]);
    setActiveId('');

    // Wait for content to render, then extract headings
    const timeoutId = setTimeout(() => {
      // Extract headings from the main content area
      const mainContent = document.querySelector('main') || document;
      const headingElements = mainContent.querySelectorAll('h2, h3');
      const extractedHeadings: Heading[] = [];

      headingElements.forEach((heading) => {
        const text = heading.textContent?.trim() || '';
        if (!text) return;

        // Generate ID from text if not present
        let id = heading.id;
        if (!id) {
          id = text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
          heading.id = id;
        }

        extractedHeadings.push({
          id,
          text,
          level: parseInt(heading.tagName.charAt(1)),
        });
      });

      setHeadings(extractedHeadings);

      // Set up intersection observer for active heading
      if (headingElements.length > 0) {
        const observer = new IntersectionObserver(
          (entries) => {
            // Find the heading that's currently in view
            const visibleHeadings = entries
              .filter((entry) => entry.isIntersecting)
              .map((entry) => ({
                id: entry.target.id,
                top: entry.boundingClientRect.top,
              }))
              .sort((a, b) => a.top - b.top);

            if (visibleHeadings.length > 0) {
              setActiveId(visibleHeadings[0].id);
            }
          },
          {
            rootMargin: '-100px 0px -66%',
            threshold: [0, 0.25, 0.5, 0.75, 1],
          }
        );

        headingElements.forEach((heading) => observer.observe(heading));

        return () => {
          headingElements.forEach((heading) => observer.unobserve(heading));
        };
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  if (headings.length === 0) {
    return null;
  }

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <aside className="hidden xl:block w-64 pl-8">
      <div className="sticky top-20">
        <h3 className="text-sm font-semibold text-textPrimary mb-4">On this page</h3>
        <nav className="space-y-1">
          {headings.map((heading) => (
            <a
              key={heading.id}
              href={`#${heading.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToHeading(heading.id);
              }}
              className={`block text-sm transition-colors ${
                heading.level === 3 ? 'pl-4' : ''
              } ${
                activeId === heading.id
                  ? 'text-gray-900 font-medium'
                  : 'text-textSecondary hover:text-textPrimary'
              }`}
            >
              {heading.text}
            </a>
          ))}
        </nav>
      </div>
    </aside>
  );
}

